import { getKeySuggestions, isValidUploadKey } from "../../utils/projectKey.util";
import { useAddProjectKey } from "../../hooks/API/projects.api";
import { toast } from "react-toast";

const ProjectKeyField = ({
  value,
  onChange,
  onErrorChange,
  error,
  setUploadProjectKey,
  projectId,
  onKeySaved,
  projectName,
  variant = 'wizard',
}) => {
  const suggestions = getKeySuggestions(projectName);
  const isValid = isValidUploadKey(value);
  const isPage = variant === 'page';

  const { mutate: addProjectKey, isLoading: addProjectLoading } = useAddProjectKey();

  async function handleAddProjectKey() {
    if (!isValid) {
      onErrorChange("Must start with a letter, 2+ chars, uppercase letters/numbers/hyphens/underscores only.");
      return;
    }

    addProjectKey(
      { projectId: projectId, projectKey: value },
      {
        onSuccess: () => {
          setUploadProjectKey(value);
          if (onKeySaved) onKeySaved(true);
        },
        onError: (err) => {
          toast.error(err.response?.data?.detail || 'Failed to add project key.');
          if (onKeySaved) onKeySaved(false);
        }
      }
    );
  }

  const inputBorderClass = error
    ? 'border-red-500 focus:ring-red-500'
    : isValid && value
      ? 'border-green-500 focus:ring-green-500'
      : 'border-gray-300 focus:ring-blue-500';

  const inputClassName = isPage
    ? `w-full px-4 py-2 border rounded-md font-mono focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 pr-10 ${inputBorderClass}`
    : 'wizard-form-input';

  const wrapperClassName = isPage ? '' : 'wizard-form-group';

  return (
    <div className={wrapperClassName} style={isPage ? undefined : { marginTop: '1.25rem' }}>
      <label className={isPage ? 'block text-sm font-medium text-gray-700 mb-1' : 'wizard-form-label'}>
        Project Key <span className={isPage ? 'text-red-500' : 'required'}>*</span>
      </label>
      <p className={isPage ? 'text-xs text-gray-500 mb-2' : undefined} style={isPage ? undefined : { fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        Uppercase letters, numbers,{' '}
        <code className={isPage ? 'bg-gray-100 px-1 rounded text-xs' : undefined} style={isPage ? undefined : { background: 'var(--bg-gray-100)', padding: '1px 4px', borderRadius: '3px', fontSize: '0.75rem' }}>-</code>
        {' '}and{' '}
        <code className={isPage ? 'bg-gray-100 px-1 rounded text-xs' : undefined} style={isPage ? undefined : { background: 'var(--bg-gray-100)', padding: '1px 4px', borderRadius: '3px', fontSize: '0.75rem' }}>_</code>
        {' '}only
      </p>

      {projectName && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-xs text-gray-500">Suggestions:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onChange(s); onErrorChange(''); }}
              className={`text-xs font-mono px-2.5 py-0.5 rounded border transition-all duration-150 ${
                value === s
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-50 text-gray-600 border-gray-300 hover:border-blue-400'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            className={inputClassName}
            placeholder="e.g. MY-PROJECT"
            value={value}
            maxLength={10}
            onChange={(e) => {
              onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9\-_]/g, ''));
              if (onKeySaved) onKeySaved(false);
            }}
            onBlur={() => {
              if (value && !isValidUploadKey(value))
                onErrorChange('Invalid format. Must start with a letter and be at least 2 characters.');
              else onErrorChange('');
            }}
            onFocus={() => onErrorChange('')}
            style={isPage ? undefined : {
              fontFamily: 'monospace',
              width: '100%',
              paddingRight: '2rem',
              borderColor: error ? 'var(--danger-color)' : isValid ? 'var(--success-color)' : undefined,
              boxShadow: error
                ? '0 0 0 3px rgba(220,53,69,0.1)'
                : isValid ? '0 0 0 3px rgba(34,197,94,0.1)' : undefined
            }}
          />
          {error && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-sm">✕</span>
          )}
          {!error && isValid && value && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-sm">✓</span>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddProjectKey}
          disabled={!isValid || addProjectLoading}
          className={`shrink-0 px-4 py-2 h-[42px] rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
            isValid && !addProjectLoading
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {addProjectLoading ? '...' : 'Add Key'}
        </button>
      </div>

      {error && (
        <p className={`text-xs mt-1 ${isPage ? 'text-red-600' : ''}`} style={isPage ? undefined : { color: 'var(--danger-color)' }}>
          {error}
        </p>
      )}
      {!error && isValid && value && (
        <p className={`text-xs mt-1 ${isPage ? 'text-green-600' : ''}`} style={isPage ? undefined : { color: 'var(--success-color)' }}>
          Looks good!
        </p>
      )}
    </div>
  );
};

export default ProjectKeyField;
